/**
 * Coastlines (and faint country borders) as static segment quads, binned into map tiles
 * (only the rows/columns in view are drawn), with one level of detail per zoom band:
 * simplified to ~0.6 px, needles narrower than ~2.5 px collapsed, islands smaller than
 * ~5 px dropped and corners rounded (Chaikin), so the coast reads as one clean line at
 * every zoom. Levels are built lazily on first use.
 * Each segment knows the land hex it belongs to, so its colour follows the current owner
 * and its style follows our knowledge: crisp inside coverage, stale-dotted outside,
 * dim dotted where we have never looked.
 */
import { chaikin, simplifyForDisplay, type LoadedWorld } from '@sa/mapdata';
import { GLSL_COMMON, makeProgram, type GL, type Program } from './gl';
import { expandQuads, QuadIndices, quadVao } from './quads';
import { GLSL_HEX } from './hexfield';
import type { FrameParams } from './frame';
import type { TerritoryLayer } from './territory';

const FLOATS = 8;

const VS = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
${GLSL_COMMON}
${GLSL_HEX}
uniform float uDpr;
uniform vec4 uOwnerCol[9];
uniform int uMe;
uniform int uAllyMask;
in vec2 aCorner;
in vec4 iAB;
in vec4 iMeta;    // s0 (mu), hex, kind (0 coast, 1 border), -
out vec2 vLocal;
out float vS;
flat out vec3 vCol;
flat out vec4 vStyle;   // stale alpha, crisp alpha, unknown, solid (own/ally: never dotted)
flat out float vLive;
flat out float vKind;
void main() {
  vec2 pa = worldToPx(iAB.xy);
  vec2 pb = worldToPx(iAB.zw);
  vec2 d = pb - pa;
  float len = length(d);
  vec2 dir = len > 1e-4 ? d / len : vec2(1.0, 0.0);
  vec2 nrm = vec2(-dir.y, dir.x);
  float half_ = 2.0 * uDpr;
  // butt ends: the next segment owns the joint, so vertices never blend twice (no beads)
  float along = aCorner.x * len;
  vLocal = vec2(along, aCorner.y * half_);
  vS = iMeta.x * uCam.z + along;
  vKind = iMeta.z;
  vLive = 0.0;
  int h = int(iMeta.y);
  vec3 grey = vec3(0.588, 0.667, 0.627);
  vCol = grey;
  vStyle = vec4(0.16, 0.16, 1.0, 0.0);
  if (h >= 0) {
    uvec4 s = hexState(h);
    bool seen = (s.a & 2u) != 0u;
    vLive = (s.a & 1u) != 0u ? 1.0 : 0.0;
    int o = int(s.r);
    if (seen) {
      if (o == uMe) { vCol = mix(vec3(0.376, 1.0, 0.627), vec3(0.84, 1.0, 0.894), 0.2); vStyle = vec4(0.2, 0.55, 0.0, 1.0); }
      // an ally's coast stays at the quiet "known" level: our own nation is the bright one
      else if (o > 0 && ((uAllyMask >> o) & 1) == 1) { vCol = vec3(0.33, 0.84, 0.886); vStyle = vec4(0.13, 0.24, 0.0, 1.0); }
      else if (o > 0) { vCol = uOwnerCol[o].rgb; vStyle = vec4(0.15, 0.85, 0.0, 0.0); }
      else { vCol = grey; vStyle = vec4(0.13, 0.42, 0.0, 0.0); }
    }
  }
  gl_Position = pxToClip(pa + dir * along + nrm * vLocal.y);
}`;

const FS = /* glsl */ `#version 300 es
precision highp float;
uniform sampler2D uVis;
uniform vec2 uView;
uniform float uDpr;
in vec2 vLocal;
in float vS;
flat in vec3 vCol;
flat in vec4 vStyle;
flat in float vLive;
flat in float vKind;
out vec4 outColor;
void main() {
  float d = abs(vLocal.y);
  float vis = texture(uVis, gl_FragCoord.xy / uView).r;
  float hw = (vKind > 0.5 ? 0.5 : 0.65) * uDpr;
  float core = 1.0 - smoothstep(hw - 0.5, hw + 0.7, d);
  float a;
  if (vKind > 0.5) {
    // country borders: faint context, only where we can see
    float dot_ = mod(vS, 4.0 * uDpr) < 1.6 * uDpr ? 1.0 : 0.0;
    a = (0.025 + 0.09 * vis) * dot_ * (1.0 - vStyle.z);
  } else {
    float dot_ = mod(vS, 3.0 * uDpr) < 1.2 * uDpr ? 1.0 : 0.0;
    float liveF = vStyle.w > 0.5 ? vis : max(vis, 0.75 * vLive);
    // outside coverage, foreign coasts turn to a stale dotted outline
    float stale = vStyle.w > 0.5 ? vStyle.x : vStyle.x * (vStyle.z > 0.5 ? dot_ : 0.35 + 0.65 * dot_);
    a = mix(stale, vStyle.y, vStyle.z > 0.5 ? 0.0 : liveF);
  }
  a *= core;
  outColor = vec4(vCol * a, a);
}`;

const TILE_COLS = 12;
const TILE_ROWS = 5;

/** Level-of-detail bands: from `zoom` (css px per mu) up to the next band. */
const LODS: readonly { zoom: number; smooth: boolean }[] = [
  { zoom: 0, smooth: false },
  { zoom: 8, smooth: false },
  { zoom: 13, smooth: false },
  { zoom: 20, smooth: true },
  { zoom: 32, smooth: true },
];
/** simplification error, css px */
const TOL_PX = 0.6;
/** slivers narrower than this collapse, css px */
const NEEDLE_PX = 2.5;
/** islands smaller than this are not outlined, css px */
const MIN_RING_PX = 5;
/** zoom used for a band's pixel budget (the lowest band starts at the world-fit zoom) */
const bandZoom = (i: number) => Math.max(5, LODS[i].zoom);

interface Lod {
  vao: WebGLVertexArrayObject;
  /** per tile (row-major) first instance; length TILE_COLS·TILE_ROWS + 1 */
  tileStart: Int32Array;
}

export class CoastLayer {
  private prog: Program;
  private idx: QuadIndices;
  private lods: (Lod | null)[] = LODS.map(() => null);
  private tileW: number;
  private tileH: number;

  constructor(
    private gl: GL,
    private world: LoadedWorld,
    private territory: TerritoryLayer,
  ) {
    this.prog = makeProgram(gl, 'coast', VS, FS, [
      'uView',
      'uCam',
      'uDpr',
      'uCells',
      'uState',
      'uFlip',
      'uWarp',
      'uWarpSize',
      'uLat',
      'uCellDim',
      'uOwnerCol',
      'uMe',
      'uAllyMask',
      'uVis',
    ]);
    this.tileW = world.width / TILE_COLS;
    this.tileH = world.height / TILE_ROWS;
    this.idx = new QuadIndices(gl);
  }

  private lod(zoomCss: number): Lod {
    let i = LODS.length - 1;
    while (i > 0 && zoomCss < LODS[i].zoom) i--;
    return (this.lods[i] ??= this.upload(buildCoastSegments(this.world, this.territory.cells, bandZoom(i), LODS[i].smooth)));
  }

  /** Sort segments into row-major tiles so a view only draws what it overlaps. */
  private upload(seg: Float32Array): Lod {
    const gl = this.gl;
    const n = seg.length / FLOATS;
    const tiles = TILE_COLS * TILE_ROWS;
    const tileOf = new Int32Array(n);
    const counts = new Int32Array(tiles + 1);
    for (let i = 0; i < n; i++) {
      const o = i * FLOATS;
      const cx = Math.min(TILE_COLS - 1, Math.max(0, Math.floor((seg[o] + seg[o + 2]) / 2 / this.tileW)));
      const cy = Math.min(TILE_ROWS - 1, Math.max(0, Math.floor((seg[o + 1] + seg[o + 3]) / 2 / this.tileH)));
      tileOf[i] = cy * TILE_COLS + cx;
      counts[tileOf[i] + 1]++;
    }
    for (let t = 0; t < tiles; t++) counts[t + 1] += counts[t];
    const fill = counts.slice(0, tiles);
    const sorted = new Float32Array(seg.length);
    for (let i = 0; i < n; i++) sorted.set(seg.subarray(i * FLOATS, (i + 1) * FLOATS), fill[tileOf[i]]++ * FLOATS);
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, expandQuads(sorted, FLOATS, n), gl.STATIC_DRAW);
    const vao = quadVao(gl, this.prog.prog, buf, FLOATS, ['iAB', 'iMeta'], this.idx);
    gl.bindVertexArray(vao);
    this.idx.ensure(n);
    gl.bindVertexArray(null);
    return { vao, tileStart: counts };
  }

  draw(f: FrameParams, visTex: WebGLTexture, view: [number, number, number, number], zoomCss: number): void {
    const gl = this.gl;
    const p = this.prog;
    gl.useProgram(p.prog);
    gl.uniform2f(p.u.uView!, f.viewW, f.viewH);
    gl.uniform3f(p.u.uCam!, f.camX, f.camY, f.zoomPx);
    gl.uniform1f(p.u.uDpr!, f.dpr);
    this.territory.bindHexUniforms(p, 0);
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, visTex);
    gl.uniform1i(p.u.uVis!, 4);
    gl.uniform4fv(p.u.uOwnerCol!, f.ownerColors);
    gl.uniform1i(p.u.uMe!, f.me);
    gl.uniform1i(p.u.uAllyMask!, f.allyMask);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    const lod = this.lod(zoomCss);
    gl.bindVertexArray(lod.vao);
    // segments are binned by midpoint: widen the view by a segment length
    const m = 3;
    const c0 = Math.max(0, Math.floor((view[0] - m) / this.tileW));
    const c1 = Math.min(TILE_COLS - 1, Math.floor((view[2] + m) / this.tileW));
    const r0 = Math.max(0, Math.floor((view[1] - m) / this.tileH));
    const r1 = Math.min(TILE_ROWS - 1, Math.floor((view[3] + m) / this.tileH));
    for (let r = r0; r <= r1 && c0 <= c1; r++) {
      const start = lod.tileStart[r * TILE_COLS + c0];
      const count = lod.tileStart[r * TILE_COLS + c1 + 1] - start;
      if (count <= 0) continue;
      gl.drawElements(gl.TRIANGLES, count * 6, gl.UNSIGNED_INT, start * 6 * 4);
    }
    gl.bindVertexArray(null);
    gl.disable(gl.BLEND);
  }
}

/** Display polylines for one zoom band (css px per mu): see the module comment. */
export function coastLod(lines: readonly Float32Array[], zoom: number, smooth: boolean): number[][] {
  const out: number[][] = [];
  for (const raw of lines) {
    const s = simplifyForDisplay(raw, TOL_PX / zoom, NEEDLE_PX / zoom, MIN_RING_PX / zoom);
    if (s) out.push(smooth ? chaikin(s) : s);
  }
  return out;
}

/** Flatten coast + border polylines into segment records tagged with their land hex. */
export function buildCoastSegments(world: LoadedWorld, cells: Int32Array, zoom: number, smooth: boolean): Float32Array {
  const { lattice } = world.extras;
  const hexAt = (x: number, y: number): number => {
    const rf = (y - lattice.originY) / (1.5 * lattice.hexR);
    const qf = (x - lattice.originX) / (Math.sqrt(3) * lattice.hexR) - rf / 2;
    const sf = -qf - rf;
    let q = Math.round(qf);
    let r = Math.round(rf);
    const s = Math.round(sf);
    if (Math.abs(q - qf) > Math.abs(r - rf) && Math.abs(q - qf) > Math.abs(s - sf)) q = -r - s;
    else if (Math.abs(r - rf) > Math.abs(s - sf)) r = -q - s;
    const col = q + Math.floor(r / 2);
    if (r < 0 || r >= lattice.rows || col < 0 || col >= lattice.cols) return -1;
    return cells[r * lattice.cols + col];
  };
  const out: number[] = [];
  const add = (lines: number[][], kind: number) => {
    for (const pl of lines) {
      let s = 0;
      for (let i = 0; i + 3 < pl.length; i += 2) {
        const ax = pl[i];
        const ay = pl[i + 1];
        const bx = pl[i + 2];
        const by = pl[i + 3];
        out.push(ax, ay, bx, by, s, hexAt((ax + bx) / 2, (ay + by) / 2), kind, 0);
        s += Math.hypot(bx - ax, by - ay);
      }
    }
  };
  add(coastLod(world.coast, zoom, smooth), 0);
  add(coastLod(world.borders, zoom, false), 1);
  return new Float32Array(out);
}
